import { saveAs } from 'file-saver';
import { useEffect, useRef, useState } from 'react';
import { ProgressBar } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import tyreTemplateFile from "../../assets/files/template-tyres.csv";
import templateFile from '../../assets/files/template.csv';
import Button from '../../components/button';
import CustomModal from '../../components/CustomModal';
import '../../styles/Dashboard.css';
import { images } from '../../utils/images';
import { closeIcon, downloadIcon, dropdownIcon, errorIcon, refreshIcon, successIcon, uploadIcon, warningIcon, wrongIcon } from '../../utils/svgs';
import { errorNotify, successNotify } from '../../utils/toast';
import { proceedFile, resetUploadData, uploadFile } from '../importAssets/ImportAssetsSlice';

const ImportAssets = ({ csvType, onImportSuccess }) => {
    const fileInputRef = useRef(null);

    const dispatch = useDispatch();
    const { uploadError, uploadLoading } = useSelector((state) => state.dashboard);

    const [loading, setLoading] = useState(false);
    const [handleUploadData, setHandleUploadData] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [localProgress, setLocalProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [showErrorDetails, setShowErrorDetails] = useState(false);
    const [assetError, setAssetError] = useState(null);
    const [showAssetModal, setShowAssetModal] = useState(false);
    const [progressPercent, setProgressPercent] = useState(0);
    const [currentCount, setCurrentCount] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);

    useEffect(() => {
        if (handleUploadData?.invalid_records?.length === 0 && handleUploadData?.valid_records > 0) {
            animateRecordProgress(handleUploadData.valid_records);
        }
        else if (handleUploadData?.invalid_records?.length > 0) {
            animateRecordProgress(handleUploadData?.invalid_records?.length);
        }
    }, [handleUploadData]);

    const closeModalHandler = () => {
        dispatch(resetUploadData());

        setHandleUploadData(null);
        setSelectedFile(null);
        setLocalProgress(0);
        setShowErrorDetails(false);
        setAssetError(null);
        setIsUploading(false);
        setProgressPercent(0);
        setCurrentCount(0);
        setTotalRecords(0);
        setShowAssetModal(false);
        setShowModal(false);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }

    const animateRecordProgress = (total) => {
        setTotalRecords(total);
        setProgressPercent(0);
        setCurrentCount(0);

        let count = 0;
        const interval = setInterval(() => {
            count += Math.ceil(total / 100);
            if (count >= total) {
                count = total;
                clearInterval(interval);
            }

            const percent = Math.round((count / total) * 100);

            setCurrentCount(count);
            setProgressPercent(percent);
        }, 60);
    };

    const simulateProgress = () => {
        setIsUploading(true);
        setLocalProgress(0);

        let progress = 0;
        const interval = setInterval(() => {
            progress += Math.floor(Math.random() * 10) + 5;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
                setTimeout(() => setIsUploading(false), 500);
            }
            setLocalProgress(progress);
        }, 200);
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file && (file.type === 'text/csv' || file.name.endsWith('.csv') || file.name.endsWith('.xls') || file.name.endsWith('.xlsx'))) {
            setSelectedFile(file);
            simulateProgress();
        } else {
            alert('Please select a valid CSV or Excel file');
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const files = event.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            if (file.type === 'text/csv' || file.name.endsWith('.csv') || file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
                setSelectedFile(file);
                simulateProgress();
            } else {
                alert('Please select a valid CSV or Excel file');
            }
        }
    };

    const handleUpload = async () => {
        if (!selectedFile || !csvType) {
            errorNotify('Please select both file and upload type');
            return;
        }

        try {
            setLoading(true);
            const response = await dispatch(uploadFile({
                file: selectedFile,
                uploadType: csvType.value
            })).unwrap();

            setHandleUploadData(response?.data);
            setLoading(false);

        } catch (error) {
            errorNotify(error);
            setLoading(false);
        }
    };

    const handleProceed = async () => {
        if (!csvType) {
            errorNotify('Please select upload type');
            return;
        }

        try {
            await dispatch(proceedFile({
                file_path: handleUploadData.file_path,
                uploadType: csvType.value
            })).unwrap();
            successNotify('Imported successfully');
            closeModalHandler();

            // Call the callback function to refresh data
            if (onImportSuccess) {
                onImportSuccess();
            }

        } catch (error) {
            errorNotify(error);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setLocalProgress(0);
        setIsUploading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDownloadTemplate = () => {
        const link = document.createElement('a');
        link.href = csvType?.value === 'tyres' ? tyreTemplateFile : templateFile;
        link.download = csvType?.value === 'tyres' ? 'tyres-template.csv' : 'maintenance-template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadFailedEntries = () => {
        const invalidRecords = handleUploadData?.invalid_records || [];

        const workbook = XLSX.utils.book_new();

        invalidRecords.forEach((record, index) => {
            const filteredKeys = Object.keys(record).filter(
                key => key !== 'maintenance_complete_date' && (record[key] === null || record[key] === "")
            );

            if (filteredKeys.length === 0) return;

            const cleanObj = {};
            filteredKeys.forEach(key => {
                const formattedKey = key
                    .replace(/_/g, ' ')
                    .replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1));
                cleanObj[formattedKey] = "";
            });

            const sheet = XLSX.utils.json_to_sheet([cleanObj]);
            XLSX.utils.book_append_sheet(workbook, sheet, `Record ${index + 1}`);
        });

        const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        saveAs(blob, 'failed_entries.xlsx');
    };

    let showData = <div className='import_assets_modal'>
        <div
            className='import_assets_modal_upload_div mt-3'
            onDragOver={!selectedFile ? handleDragOver : undefined}
            onDrop={!selectedFile ? handleDrop : undefined}
            onClick={!selectedFile ? () => fileInputRef.current?.click() : undefined}
            style={{ cursor: !selectedFile ? 'pointer' : 'default' }}
        >
            {
                localProgress === 100 ?
                    <span> <b onClick={handleRemoveFile}>{closeIcon}</b> <img src={images.csvFileIcon} alt='' height={120} />
                        {selectedFile?.name}
                    </span> :
                    <span>{uploadIcon}</span>
            }

            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".csv,.xls,.xlsx"
                style={{ display: 'none' }}
            />
        </div>

        {
            !selectedFile &&
            <div className='import_assets_modal_text_div'>
                <p>Drag file or Browse to select. <br /> Allowed types .xls or .csv file</p>
                <div onClick={handleDownloadTemplate} style={{ cursor: 'pointer' }}>{downloadIcon} Download Template</div>
            </div>
        }

        {uploadError && (
            <div style={{ color: 'red', marginBottom: '10px' }}>
                {uploadError}
            </div>
        )}

        {(selectedFile && localProgress !== 100) && (
            <div className='uploaded_files_div'>
                <div className='uploaded_file'>
                    <img src={images.csvFileIcon} alt='' />
                    <div>
                        <div>
                            <h6>{selectedFile?.name}</h6>
                            {!isUploading && (
                                <span onClick={handleRemoveFile} style={{ cursor: 'pointer' }}>
                                    {closeIcon}
                                </span>
                            )}
                        </div>
                        <div className='mt-2'>
                            <p>{formatFileSize(selectedFile?.size)}</p>
                            {isUploading && <span>{localProgress}%</span>}
                        </div>
                    </div>
                </div>
                {isUploading && <ProgressBar now={localProgress} />}
            </div>
        )}


        <div className='mt-5'>
            <Button
                cancelBtn={closeModalHandler}
                submitBtn={handleUpload}
                loading={loading}
                disabled={!selectedFile || isUploading}
                submitText="Save"
            />
        </div>
    </div>

    if (handleUploadData?.invalid_records?.length === 0 && handleUploadData?.valid_records === 0) {
        showData = <div>
            <div className='verify_integrity_main'>
                <div>{warningIcon}</div>
                <div>
                    <p>Verifying Data Integrity </p>
                    <ProgressBar now={100} />
                    <div className='verify_integrity_progress'>
                        <span>100%</span>
                        <span>(0 / 0)</span>
                    </div>
                </div>
            </div>
            <div className='verify_integrity_not_found'>
                <p>{warningIcon} File Empty: No Data Found!</p>
            </div>
            <div className='mt-5'>
                <Button
                    cancelBtn={closeModalHandler}
                    submitBtn={handleProceed}
                    loading={uploadLoading}
                    disabled={!selectedFile || !csvType || isUploading}
                    submitText="Proceed"
                />
            </div>
        </div>
    }

    else if (handleUploadData?.invalid_records?.length === 0 && handleUploadData?.valid_records > 0) {
        showData = <div>
            <div className='verify_integrity_main success'>
                <div>{progressPercent !== 100 ? refreshIcon : successIcon}</div>
                <div>
                    <p>{progressPercent !== 100 ? "Verifying Data Integrity" : "Importing Assets"} </p>
                    <ProgressBar now={progressPercent} />
                    <div className='verify_integrity_progress'>
                        <span>{progressPercent}%</span>
                        <span>({currentCount} / {totalRecords})</span>
                    </div>
                </div>
            </div>

            <div className='mt-5'>
                <Button
                    cancelBtn={closeModalHandler}
                    submitBtn={handleProceed}
                    loading={uploadLoading}
                    disabled={progressPercent !== 100}
                    submitText="Save"
                    isShowCancel={false}
                />
            </div>
        </div>
    }

    else if (handleUploadData?.invalid_records?.length > 0) {
        showData = <div>
            <div className='verify_integrity_main'>
                <div>{progressPercent !== 100 ? refreshIcon : warningIcon}</div>
                <div>
                    <p>Verifying Data Integrity </p>
                    <ProgressBar now={progressPercent} />
                    <div className='verify_integrity_progress'>
                        <span>{progressPercent}%</span>
                        <span>({currentCount} / {totalRecords})</span>
                    </div>
                </div>
            </div>
            <div className='verify_integrity_error'>
                <p onClick={() => setShowErrorDetails(!showErrorDetails)}>{totalRecords} Asset Data Integrity Failure {dropdownIcon}</p>

                {
                    showErrorDetails &&
                    <div className='verify_integrity_error_details'>
                        {
                            handleUploadData?.invalid_records.map((item, index) => (
                                <div>
                                    <p>{wrongIcon} Fields Value are missing in Row {index + 1}</p>
                                    <span onClick={() => {
                                        setAssetError(item)
                                        setShowAssetModal(true);
                                    }}>Failure Reason</span>
                                </div>
                            ))
                        }
                    </div>
                }
            </div>
            <div className='download_failed_entries'>
                <p onClick={handleDownloadFailedEntries}>{downloadIcon} Download Failed Entries</p>
                <div>
                    <span>{errorIcon}</span>
                    <p>On clicking proceed failed entries will not be imported, correct issues mentioned and upload again!</p>
                </div>
            </div>
            <div className='mt-5'>
                <Button
                    cancelBtn={closeModalHandler}
                    submitBtn={handleProceed}
                    loading={uploadLoading}
                    disabled={!selectedFile || !csvType || isUploading}
                    submitText="Proceed"
                />
            </div>
        </div>
    }

    const emptyFields = assetError ? Object.keys(assetError).filter(
        key => assetError[key] === "" || assetError[key] === null
    ) : [];

    return (
        <div className='dashboard_main_wrapper'>
            <CustomModal show={showAssetModal} handleClose={() => setShowAssetModal(false)} heading="Failure Reason" size="md">
                <ul className='asset_error_list'>
                    {emptyFields?.map((field, index) => (
                        <li key={index}>{field.replace(/_/g, ' ')}</li>
                    ))}
                </ul>
            </CustomModal>

            <CustomModal show={showModal} handleClose={closeModalHandler}>
                {showData}
            </CustomModal>
            <button onClick={() => setShowModal(true)}>Import</button>
        </div>
    )
}

export default ImportAssets